/**
 * Hikari Vault - Client-side Cryptography
 * ========================================
 * Everything in here runs in the browser. Plaintext never leaves the device.
 *
 *   - deriveKey(secret, salt) → AES-256-GCM CryptoKey via PBKDF2 (120k iters)
 *   - encryptBlob(obj, key)    → base64(iv || ciphertext)
 *   - decryptBlob(b64,  key)   → obj | null
 *   - wrapKey(rawMaster, kek)  → base64 wrapped master key
 *   - unwrapKey(b64,    kek)   → rawMaster | null
 *   - generateMasterKey()      → Uint8Array(32)  [random 256-bit]
 *   - importMasterKey(raw)     → CryptoKey usable as AES-GCM
 *   - registerBiometric()      → base64 rawId | null
 *   - verifyBiometric(rawIdB64)→ boolean
 *   - generateRecoveryPhrase() → 12-word string (256 bits of entropy truncated to 128)
 *   - b64 / fromB64 helpers
 */

import { WORDLIST } from "./wordlist";

const SALT_PREFIX = "hikari-vault-v2::";

// ── base64 helpers ──
export const b64 = (bytes) =>
    btoa(String.fromCharCode(...new Uint8Array(bytes)));
export const fromB64 = (s) =>
    Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

// ── PBKDF2 → AES-GCM key-encryption-key (KEK) ──
export async function deriveKEK(secret, saltContext) {
    const enc = new TextEncoder();
    const km = await crypto.subtle.importKey(
        "raw",
        enc.encode(secret),
        "PBKDF2",
        false,
        ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: enc.encode(SALT_PREFIX + saltContext),
            iterations: 120000,
            hash: "SHA-256",
        },
        km,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );
}

// ── Master key (256-bit random, AES-GCM) ──
export function generateMasterKey() {
    return crypto.getRandomValues(new Uint8Array(32));
}

export async function importMasterKey(rawBytes) {
    return crypto.subtle.importKey(
        "raw",
        rawBytes,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );
}

// ── Encrypt any JSON-serialisable object with a CryptoKey ──
export async function encryptBlob(obj, key) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const pt = new TextEncoder().encode(JSON.stringify(obj));
    const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, pt);
    const buf = new Uint8Array(iv.byteLength + ct.byteLength);
    buf.set(iv, 0);
    buf.set(new Uint8Array(ct), iv.byteLength);
    return b64(buf);
}

export async function decryptBlob(b64Str, key) {
    try {
        const buf = fromB64(b64Str);
        const iv = buf.slice(0, 12);
        const ct = buf.slice(12);
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            ct,
        );
        return JSON.parse(new TextDecoder().decode(pt));
    } catch {
        return null;
    }
}

// ── Wrap / unwrap raw master key bytes with a KEK ──
export async function wrapMasterKey(rawMasterBytes, kek) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        kek,
        rawMasterBytes,
    );
    const buf = new Uint8Array(iv.byteLength + ct.byteLength);
    buf.set(iv, 0);
    buf.set(new Uint8Array(ct), iv.byteLength);
    return b64(buf);
}

export async function unwrapMasterKey(b64Str, kek) {
    try {
        const buf = fromB64(b64Str);
        const iv = buf.slice(0, 12);
        const ct = buf.slice(12);
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            kek,
            ct,
        );
        return new Uint8Array(pt);
    } catch {
        return null;
    }
}

// ── WebAuthn (Face ID / Touch ID / Windows Hello) ──
export async function registerBiometric() {
    if (!window.PublicKeyCredential) return null;
    try {
        const cred = await navigator.credentials.create({
            publicKey: {
                challenge: crypto.getRandomValues(new Uint8Array(32)),
                rp: {
                    name: "Hikari Vault",
                    id: window.location.hostname || "localhost",
                },
                user: {
                    id: crypto.getRandomValues(new Uint8Array(16)),
                    name: "vault-owner",
                    displayName: "Vault Owner",
                },
                pubKeyCredParams: [
                    { alg: -7, type: "public-key" },
                    { alg: -257, type: "public-key" },
                ],
                authenticatorSelection: {
                    authenticatorAttachment: "platform",
                    userVerification: "required",
                },
                timeout: 60000,
            },
        });
        return cred ? b64(cred.rawId) : null;
    } catch {
        return null;
    }
}

export async function verifyBiometric(rawIdB64) {
    if (!window.PublicKeyCredential) return false;
    try {
        const rawId = fromB64(rawIdB64);
        const assertion = await navigator.credentials.get({
            publicKey: {
                challenge: crypto.getRandomValues(new Uint8Array(32)),
                allowCredentials: [{ id: rawId, type: "public-key" }],
                userVerification: "required",
                timeout: 60000,
            },
        });
        return !!assertion;
    } catch {
        return false;
    }
}

export async function isBiometricAvailable() {
    if (!window.PublicKeyCredential) return false;
    try {
        return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
        return false;
    }
}

// ── Recovery phrase (12 words from BIP39-lite wordlist) ──
// 12 words × ~10.97 bits each ≈ 132 bits of entropy. Solid.
export function generateRecoveryPhrase() {
    const words = [];
    const random = crypto.getRandomValues(new Uint32Array(12));
    for (let i = 0; i < 12; i++) {
        words.push(WORDLIST[random[i] % WORDLIST.length]);
    }
    return words.join(" ");
}

export function normalizeRecoveryPhrase(input) {
    return input.trim().toLowerCase().replace(/\s+/g, " ");
}

// ── UUID v4 (used for per-device sub-identifiers when needed) ──
export function uuid() {
    if (crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const h = [...bytes].map((b) => b.toString(16).padStart(2, "0"));
    return `${h.slice(0, 4).join("")}-${h.slice(4, 6).join("")}-${h.slice(6, 8).join("")}-${h.slice(8, 10).join("")}-${h.slice(10, 16).join("")}`;
}

// ── Vault ID derivation ──
// Deterministic: the same recovery phrase always yields the same vault_id,
// so a new device can LOCATE its backend record knowing only the phrase.
// The vault stays cryptographically sealed — finding the document still
// doesn't reveal anything without deriving the master key via PBKDF2.
export async function deriveVaultId(recoveryPhrase) {
    const normalized = normalizeRecoveryPhrase(recoveryPhrase);
    const data = new TextEncoder().encode(
        SALT_PREFIX + "vault-id::" + normalized,
    );
    const digest = await crypto.subtle.digest("SHA-256", data);
    const hex = [...new Uint8Array(digest)]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    // 32 hex chars → keep the URL short while still 128 bits of space
    return hex.slice(0, 32);
}
